import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ConfirmDialog } from '../../../components/confirm-dialog.js';
import {
  type StructureNode,
  parseNodeDependents,
  structureApi,
} from '../../../lib/structure-api.js';
import { useCurrentProject } from '../../../lib/use-project.js';

// #46: Projektstruktur pflegbar (Design-Spec 3.7 Admin/Projektkonfiguration). Anlegbar sind nur
// teilprojekt + stream (modul ist tot); Hierarchie bleibt in V1 flach (Nutzerentscheidung). Löschen
// wird blockiert, solange Rollen-Scopes/Prozess-Etiketten/Sign-offs hängen — die Zahlen werden
// benannt. Der Abschnitt ist bereits über die Admin-Seite auf project.configure gegated.

const KINDS: StructureNode['kind'][] = ['teilprojekt', 'stream'];

export function StructurePanel() {
  const { t } = useTranslation();
  const { project, isLoading: projectLoading } = useCurrentProject();
  const projectId = project?.project_id ?? '';
  const queryClient = useQueryClient();

  const { data: nodes, isLoading } = useQuery({
    queryKey: ['structure-nodes', projectId],
    queryFn: () => structureApi.list(projectId),
    enabled: Boolean(projectId),
  });

  const [newKind, setNewKind] = useState<StructureNode['kind']>('stream');
  const [newName, setNewName] = useState('');
  const [deleteErrors, setDeleteErrors] = useState<Record<string, string>>({});
  // #96: Rückfrage vor dem Löschen — der bestehende 409-Fehlerpfad (deleteErrors) bleibt
  // unverändert, er greift weiterhin erst NACH dem Bestätigen, wenn remove.mutate tatsächlich läuft.
  const [confirmDeleteNode, setConfirmDeleteNode] = useState<{ id: string; name: string } | null>(
    null,
  );

  const invalidate = () =>
    void queryClient.invalidateQueries({ queryKey: ['structure-nodes', projectId] });

  const create = useMutation({
    mutationFn: () => structureApi.create(projectId, { kind: newKind, name: newName.trim() }),
    meta: { successToast: 'feedback.created' },
    onSuccess: () => {
      invalidate();
      setNewName('');
    },
  });

  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      structureApi.rename(projectId, id, name),
    meta: { successToast: 'feedback.saved' },
    onSuccess: invalidate,
  });

  // errorToast: false — die 409-Blockade wird namentlich in der Zeile gezeigt (welche
  // Abhängigkeiten hängen), ein generischer globaler Toast wäre die zweite, unspezifische Meldung.
  const remove = useMutation({
    mutationFn: (id: string) => structureApi.remove(projectId, id),
    meta: { successToast: 'feedback.deleted', errorToast: false },
    onMutate: (id: string) => {
      setDeleteErrors((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    },
    onSuccess: () => {
      invalidate();
      setConfirmDeleteNode(null);
    },
    onError: (err: unknown, id: string) => {
      const dep = err instanceof Error ? parseNodeDependents(err.message) : null;
      const msg = dep
        ? t('admin.structure.blocked', {
            roles: dep.roles,
            processes: dep.processes,
            signoffs: dep.signoffs,
            e2e: dep.e2e_processes ?? 0,
          })
        : t('catalog.errors.generic');
      setDeleteErrors((prev) => ({ ...prev, [id]: msg }));
      setConfirmDeleteNode(null);
    },
  });

  if (projectLoading || isLoading) {
    return <div className="p-6 text-text-muted">{t('common.loading')}</div>;
  }
  if (!project) {
    return <div className="p-6 text-text-muted">{t('catalog.noProject')}</div>;
  }

  const rows = nodes ?? [];

  return (
    <div className="space-y-4 p-6">
      <div>
        <h2 className="text-lg font-bold text-text-primary">{t('admin.structure.title')}</h2>
        <p className="mt-1 text-xs text-text-muted">{t('admin.structure.hint')}</p>
        {/* TF-006: Dieses Panel legt Knoten AN, benennt sie um und löscht sie. Das UMHÄNGEN von
            Inhalten (Testfall → Prozess, Prozess → Teilprojekt, Kette → Stream) ist Katalogpflege
            und hat eine eigene Seite. Ohne diesen Verweis suchte man die Funktion genau hier —
            unter dem Namen, der schon vergeben ist. */}
        <p className="mt-2 text-xs text-text-muted">
          {t('admin.structure.assignmentHint')}{' '}
          <Link to="/assignment" className="text-brand-accent underline">
            {t('nav.assignment')}
          </Link>
        </p>
      </div>

      {/* Anlegen */}
      <section className="flex flex-wrap items-end gap-2 rounded-card border border-[var(--border)] bg-bg-surface p-4">
        <label className="flex flex-col gap-1 text-xs text-text-muted">
          {t('admin.structure.kind')}
          <select
            aria-label={t('admin.structure.kind')}
            value={newKind}
            onChange={(e) => setNewKind(e.target.value as StructureNode['kind'])}
            className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-primary"
          >
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`admin.structure.kindLabel.${k}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-1 flex-col gap-1 text-xs text-text-muted">
          {t('admin.structure.name')}
          <input
            aria-label={t('admin.structure.name')}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-primary"
          />
        </label>
        <button
          type="button"
          onClick={() => create.mutate()}
          disabled={newName.trim() === '' || create.isPending}
          className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
        >
          {t('admin.structure.add')}
        </button>
      </section>

      {/* Liste */}
      {rows.length === 0 ? (
        <div className="text-sm text-text-muted">{t('admin.structure.empty')}</div>
      ) : (
        <ul className="space-y-2">
          {rows.map((n) => (
            <StructureRow
              key={n.node_id}
              node={n}
              onRename={(name) => rename.mutate({ id: n.node_id, name })}
              onDeleteRequest={() => setConfirmDeleteNode({ id: n.node_id, name: n.name })}
              error={deleteErrors[n.node_id] ?? null}
            />
          ))}
        </ul>
      )}

      {confirmDeleteNode && (
        <ConfirmDialog
          title={t('admin.structure.delete')}
          message={t('admin.structure.confirmDelete', { name: confirmDeleteNode.name })}
          confirmLabel={t('admin.structure.delete')}
          danger
          pending={remove.isPending}
          onCancel={() => setConfirmDeleteNode(null)}
          onConfirm={() => remove.mutate(confirmDeleteNode.id)}
        />
      )}
    </div>
  );
}

function StructureRow({
  node,
  onRename,
  onDeleteRequest,
  error,
}: {
  node: StructureNode;
  onRename: (name: string) => void;
  onDeleteRequest: () => void;
  error: string | null;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(node.name);
  const dirty = name.trim() !== '' && name !== node.name;

  return (
    <li className="space-y-1 rounded-card border border-[var(--border)] p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-chip border border-[var(--border)] px-2 py-0.5 text-[11px] uppercase tracking-wide text-text-muted">
          {t(`admin.structure.kindLabel.${node.kind}`)}
        </span>
        <input
          aria-label={t('admin.structure.name')}
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="flex-1 rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-primary"
        />
        <span className="text-xs text-text-muted">
          {/* #112: Ein Stream traegt Ketten, ein Teilprojekt Prozesse. „12 Prozesse" an einem
              Stream waere eine Falschaussage — die Zahl stimmt, die Sache nicht. */}
          {node.kind === 'stream'
            ? t('admin.structure.e2eCount', { count: node.e2e_count })
            : t('admin.structure.processCount', { count: node.process_count })}
        </span>
        <button
          type="button"
          onClick={() => onRename(name.trim())}
          disabled={!dirty}
          className="rounded-btn border border-[var(--border)] px-3 py-1 text-sm text-text-primary hover:border-brand-accent disabled:opacity-50"
        >
          {t('admin.structure.save')}
        </button>
        <button
          type="button"
          onClick={onDeleteRequest}
          className="rounded-btn px-3 py-1 text-sm text-status-fail hover:underline"
        >
          {t('admin.structure.delete')}
        </button>
      </div>
      {error && <div className="text-xs text-status-fail">{error}</div>}
    </li>
  );
}
